/**
 * Admin Group Policy API
 *
 * Requires admin session token (X-Session-Token with org_mode = true).
 * All calls go to /group/* endpoints.
 *
 * GET endpoints require orgId passed explicitly (backend reads from query param).
 * POST/PUT/DELETE endpoints derive org_id from the session token in middleware.
 */

import api from "./index";
import ENDPOINTS from "./endpoints";
import logger from "../services/Logger";

// ─── Groups ───────────────────────────────────────────────────────────────────

/**
 * List all groups for an organisation.
 * @param {string} orgId
 * @returns {Promise<Array>} Array of group objects
 */
export const listAdminGroups = async (orgId) => {
  try {
    const response = await api({
      method: "GET",
      url: ENDPOINTS.ADMIN_LIST_GROUPS(orgId),
    });
    return response.data.groups;
  } catch (error) {
    logger.error("adminGroups", "Error listing groups", { error: error.message, orgId });
    throw error;
  }
};

/**
 * Get a single group document.
 * @param {string} groupId
 * @param {string} orgId
 * @returns {Promise<Object>} Group document
 */
export const getAdminGroup = async (groupId, orgId) => {
  try {
    const response = await api({
      method: "GET",
      url: ENDPOINTS.ADMIN_GET_GROUP(groupId, orgId),
    });
    return response.data;
  } catch (error) {
    logger.error("adminGroups", "Error getting group", { error: error.message, groupId });
    throw error;
  }
};

/**
 * Create a new Photon-native group.
 * @param {Object} data
 * @param {string} data.display_name
 * @param {string} [data.description]
 * @returns {Promise<{group_id: string}>}
 */
export const createAdminGroup = async ({ display_name, description = "" }) => {
  try {
    const response = await api({
      method: "POST",
      url: ENDPOINTS.ADMIN_CREATE_GROUP,
      data: { display_name, description, source: "photon_native" },
    });
    return response.data;
  } catch (error) {
    logger.error("adminGroups", "Error creating group", { error: error.message, display_name });
    throw error;
  }
};

/**
 * Update mutable fields on a group (display_name, description).
 * @param {string} groupId
 * @param {Object} updates - Partial group fields to update
 * @returns {Promise<{updated: boolean}>}
 */
export const updateAdminGroup = async (groupId, updates) => {
  try {
    const response = await api({
      method: "PUT",
      url: ENDPOINTS.ADMIN_UPDATE_GROUP(groupId),
      data: updates,
    });
    return response.data;
  } catch (error) {
    logger.error("adminGroups", "Error updating group", { error: error.message, groupId });
    throw error;
  }
};

/**
 * Delete a group.
 * @param {string} groupId
 * @returns {Promise<{deleted: boolean}>}
 */
export const deleteAdminGroup = async (groupId) => {
  try {
    const response = await api({
      method: "DELETE",
      url: ENDPOINTS.ADMIN_DELETE_GROUP(groupId),
    });
    return response.data;
  } catch (error) {
    // Preserve the backend error message for 409 Conflict so callers can
    // surface a meaningful reason to the admin (active policy / has members).
    logger.error("adminGroups", "Error deleting group", { error: error.message, groupId });
    throw error;
  }
};

// ─── Members ──────────────────────────────────────────────────────────────────

/**
 * List all members of a group.
 * @param {string} groupId
 * @param {string} orgId
 * @returns {Promise<Array>} Array of member objects
 */
export const listGroupMembers = async (groupId, orgId) => {
  try {
    const response = await api({
      method: "GET",
      url: ENDPOINTS.ADMIN_LIST_GROUP_MEMBERS(groupId, orgId),
    });
    return response.data.members;
  } catch (error) {
    logger.error("adminGroups", "Error listing group members", { error: error.message, groupId });
    throw error;
  }
};

/**
 * Add a user to a group (manual membership).
 * @param {string} groupId
 * @param {string} userId
 * @param {string} [source="manual"]
 * @returns {Promise<{added: boolean}>}
 */
export const addGroupMember = async (groupId, userId, source = "manual") => {
  try {
    const response = await api({
      method: "POST",
      url: ENDPOINTS.ADMIN_ADD_GROUP_MEMBER(groupId),
      data: { user_id: userId, source },
    });
    return response.data;
  } catch (error) {
    logger.error("adminGroups", "Error adding group member", { error: error.message, groupId, userId });
    throw error;
  }
};

/**
 * Remove a user from a group.
 * @param {string} groupId
 * @param {string} userId
 * @returns {Promise<{removed: boolean}>}
 */
export const removeGroupMember = async (groupId, userId) => {
  try {
    const response = await api({
      method: "DELETE",
      url: ENDPOINTS.ADMIN_REMOVE_GROUP_MEMBER(groupId, userId),
    });
    return response.data;
  } catch (error) {
    logger.error("adminGroups", "Error removing group member", { error: error.message, groupId, userId });
    throw error;
  }
};

/**
 * List all groups a user belongs to.
 * @param {string} userId
 * @param {string} orgId
 * @returns {Promise<Array>} Array of group objects
 */
export const getUserGroups = async (userId, orgId) => {
  try {
    const response = await api({
      method: "GET",
      url: ENDPOINTS.ADMIN_GET_USER_GROUPS(userId, orgId),
    });
    return response.data.groups;
  } catch (error) {
    logger.error("adminGroups", "Error getting user groups", { error: error.message, userId });
    throw error;
  }
};

/**
 * Get the effective merged capability policy for a user.
 * Merges all group policies + org default for the given user.
 * @param {string} userId
 * @param {string} orgId
 * @returns {Promise<Object>} Resolved policy document
 */
export const getUserResolvedPolicy = async (userId, orgId) => {
  try {
    const response = await api({
      method: "GET",
      url: ENDPOINTS.ADMIN_GET_USER_RESOLVED_POLICY(userId, orgId),
    });
    return response.data;
  } catch (error) {
    logger.error("adminGroups", "Error getting resolved policy for user", { error: error.message, userId });
    throw error;
  }
};

// ─── Group Policy ─────────────────────────────────────────────────────────────

/**
 * Get the capability policy for a group.
 * @param {string} groupId
 * @param {string} orgId
 * @returns {Promise<Object>} Policy document
 */
export const getGroupPolicy = async (groupId, orgId) => {
  try {
    const response = await api({
      method: "GET",
      url: ENDPOINTS.ADMIN_GET_GROUP_POLICY(groupId, orgId),
    });
    return response.data;
  } catch (error) {
    logger.error("adminGroups", "Error getting group policy", { error: error.message, groupId });
    throw error;
  }
};

/**
 * Replace the capability policy for a group.
 * @param {string} groupId
 * @param {Object} policy - Full policy document
 * @returns {Promise<{updated: boolean}>}
 */
export const setGroupPolicy = async (groupId, policy) => {
  try {
    const response = await api({
      method: "PUT",
      url: ENDPOINTS.ADMIN_SET_GROUP_POLICY(groupId),
      data: policy,
    });
    return response.data;
  } catch (error) {
    logger.error("adminGroups", "Error setting group policy", { error: error.message, groupId });
    throw error;
  }
};

// ─── Org Default Policy ───────────────────────────────────────────────────────

/**
 * Get the org-level default capability policy.
 * @param {string} orgId
 * @returns {Promise<Object>} Default policy document
 */
export const getOrgDefaultPolicy = async (orgId) => {
  try {
    const response = await api({
      method: "GET",
      url: ENDPOINTS.ADMIN_GET_ORG_DEFAULT_POLICY(orgId),
    });
    return response.data;
  } catch (error) {
    logger.error("adminGroups", "Error getting org default policy", { error: error.message, orgId });
    throw error;
  }
};

/**
 * Replace the org-level default capability policy.
 * @param {Object} policy - Full default policy document
 * @returns {Promise<{updated: boolean}>}
 */
export const setOrgDefaultPolicy = async (policy) => {
  try {
    const response = await api({
      method: "PUT",
      url: ENDPOINTS.ADMIN_SET_ORG_DEFAULT_POLICY,
      data: policy,
    });
    return response.data;
  } catch (error) {
    logger.error("adminGroups", "Error setting org default policy", { error: error.message });
    throw error;
  }
};

// ─── Audit Log ────────────────────────────────────────────────────────────────

/**
 * Fetch the audit log for a group.
 * @param {string} groupId
 * @param {string} orgId
 * @param {number} [limit=50]
 * @returns {Promise<Array>} Array of audit event objects
 */
export const getGroupAuditLog = async (groupId, orgId, limit = 50) => {
  try {
    const url = `${ENDPOINTS.ADMIN_GET_GROUP_AUDIT_LOG(groupId, orgId)}&limit=${limit}`;
    const response = await api({ method: "GET", url });
    return response.data.events;
  } catch (error) {
    logger.error("adminGroups", "Error fetching audit log", { error: error.message, groupId });
    throw error;
  }
};
